import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Power, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { TemporaryPasswordNotice } from '../components/temporary-password';
import { useToast } from '../components/toast';
import { Badge, Button, Card, ConfirmDialog, ErrorState, Field, Input, Modal, PageHeader, PageLoader, Table, Td, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { formatRelative } from '../lib/format';
import type { PlatformAdmin } from '../lib/types';

interface AdminList {
  /** The signed-in administrator, who can't turn their own account off. */
  you: string;
  admins: PlatformAdmin[];
}

/** Edvora's own people: who can run Edvora itself, across every school. Not a school's users. */
export function PlatformAdminsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['platform', 'admins'], queryFn: () => api.get<AdminList>('/platform/admins') });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', name: '' });
  const [editing, setEditing] = useState<PlatformAdmin | null>(null);
  const [edit, setEdit] = useState({ email: '', name: '' });
  const [removing, setRemoving] = useState<PlatformAdmin | null>(null);
  const [password, setPassword] = useState<{ email: string; password: string } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['platform', 'admins'] });
  const failed = (err: unknown) => toast.error(errorMessage(err));

  const add = useMutation({
    mutationFn: () => api.post<{ admin: PlatformAdmin; temporaryPassword?: string }>('/platform/admins', form),
    onSuccess: (result) => {
      void refresh();
      setAdding(false);
      setForm({ email: '', name: '' });
      if (result.temporaryPassword) setPassword({ email: result.admin.email, password: result.temporaryPassword });
      else toast.success(`${result.admin.email} can now run Edvora, with the password they already have`);
    },
    onError: failed,
  });

  const save = useMutation({
    mutationFn: () => api.patch<PlatformAdmin>(`/platform/admins/${editing!.id}`, edit),
    onSuccess: () => {
      void refresh();
      setEditing(null);
      toast.success('Saved');
    },
    onError: failed,
  });

  const setDisabled = useMutation({
    mutationFn: ({ admin, disabled }: { admin: PlatformAdmin; disabled: boolean }) => api.patch(`/platform/admins/${admin.id}`, { disabled }),
    onSuccess: (_result, { disabled }) => {
      void refresh();
      toast.success(disabled ? 'Account turned off, and signed out everywhere' : 'Account turned back on');
    },
    onError: failed,
  });

  const reset = useMutation({
    mutationFn: (admin: PlatformAdmin) => api.post<{ temporaryPassword: string }>(`/platform/admins/${admin.id}/reset-password`).then((result) => ({ ...result, email: admin.email })),
    onSuccess: (result) => {
      void refresh();
      setPassword({ email: result.email, password: result.temporaryPassword });
    },
    onError: failed,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/platform/admins/${removing!.id}`),
    onSuccess: () => {
      void refresh();
      toast.success(`${removing?.email} can no longer run Edvora`);
      setRemoving(null);
    },
    onError: failed,
  });

  const admins = list.data?.admins ?? [];
  const you = list.data?.you;

  return (
    <>
      <PageHeader
        title="Edvora administrators"
        description="Who can run Edvora itself: every school, the platform pages, and support sign-in. A school’s own users are managed in that school’s Settings."
        actions={
          <Button variant="primary" icon={<UserPlus />} onClick={() => setAdding(true)}>
            Add administrator
          </Button>
        }
      />
      <Card>
        {list.isLoading ? (
          <PageLoader />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Administrator</Th>
                <Th>Signs in with</Th>
                <Th>Also in</Th>
                <Th>Last signed in</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {admins.map((admin) => {
                const self = admin.id === you;
                return (
                  <tr key={admin.id} className={admin.disabled ? 'bg-slate-50/60' : undefined}>
                    <Td>
                      <p className="font-medium text-slate-900">
                        {admin.name}
                        {self && <span className="ml-2 text-xs font-normal text-slate-400">(you)</span>}
                        {admin.disabled && (
                          <Badge tone="amber" className="ml-2">
                            Turned off
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">{admin.email}</p>
                    </Td>
                    <Td className="text-xs text-slate-500">
                      {[admin.usesGoogle ? 'Google' : null, admin.hasPassword ? 'Password' : null].filter(Boolean).join(' · ') || 'Nothing yet'}
                    </Td>
                    <Td className="text-xs text-slate-500">{admin.schools === 0 ? 'No school' : `${admin.schools} school${admin.schools === 1 ? '' : 's'}`}</Td>
                    <Td className="whitespace-nowrap text-slate-500">{admin.lastLoginAt ? formatRelative(admin.lastLoginAt) : 'Never'}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Pencil />}
                        onClick={() => {
                          setEditing(admin);
                          setEdit({ email: admin.email, name: admin.name });
                        }}
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" icon={<KeyRound />} loading={reset.isPending && reset.variables?.id === admin.id} onClick={() => reset.mutate(admin)}>
                        Reset password
                      </Button>
                      {!self && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={admin.disabled ? <Power /> : <ShieldOff />}
                            loading={setDisabled.isPending && setDisabled.variables?.admin.id === admin.id}
                            onClick={() => setDisabled.mutate({ admin, disabled: !admin.disabled })}
                          >
                            {admin.disabled ? 'Turn on' : 'Turn off'}
                          </Button>
                          <Button size="sm" variant="ghost" icon={<ShieldCheck />} onClick={() => setRemoving(admin)}>
                            Remove access
                          </Button>
                        </>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        <p className="border-t border-slate-100 px-5 py-4 text-xs text-slate-500">
          An Edvora administrator can open every school as an administrator, so keep this list short. Taking access away leaves the account and whatever schools it belongs to;
          turning it off signs it out everywhere and stops it signing in at all.
        </p>
      </Card>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        size="sm"
        title="Add an Edvora administrator"
        description="A new account gets a password to pass on. An account that already exists keeps its password and its schools."
        footer={
          <>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>
              Add administrator
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} autoFocus />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        size="sm"
        title={`Edit ${editing?.name ?? ''}`}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            <Input value={edit.name} onChange={(event) => setEdit({ ...edit, name: event.target.value })} autoFocus />
          </Field>
          <Field label="Email" required hint="They sign in with this address.">
            <Input type="email" value={edit.email} onChange={(event) => setEdit({ ...edit, email: event.target.value })} />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Remove Edvora access from ${removing?.name ?? ''}?`}
        description={`${removing?.email ?? 'They'} will no longer be able to open other schools or the platform pages. Their account stays, with any schools it belongs to.`}
        confirmLabel="Remove access"
      />

      {password && <TemporaryPasswordNotice {...password} onClose={() => setPassword(null)} />}
    </>
  );
}
